import type { BaseServiceOptions, Pagination } from '@/core/types'
import { serviceConfig, filesServiceHandlers, TFile, TFiles } from '../../..'
import { useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { toValue } from 'vue'
import { useI18n } from 'vue-i18n'

const { model } = serviceConfig.files

export const useFilesQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage = 1, enableQuery, filters } = options
  const { t } = useI18n()

  const { errorHttpNotification, successNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TFile>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const addRecord = useMutation<TFile, AxiosError, TFile>(
    (payload: TFile) => {
      const uploadForm = new FormData()
      payload.files?.forEach((file) => {
        uploadForm.append('file[]', file)
      })
      uploadForm.append(
        'invitation_id',
        payload.invitation_id?.toString() ?? '',
      )

      return filesServiceHandlers.addRecord(uploadForm)
    },
    {
      onSuccess: () => serviceHandlers.invalidateQueries(),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TFiles, AxiosError>(
    [model, currentPage, filters?.loadRecords],
    () =>
      filesServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onError: (error) => errorHttpNotification(error),
    },
  )

  const deleteRecords = useMutation<void, AxiosError, Array<number>>(
    (payload) => filesServiceHandlers.deleteRecords({ ids: payload }),
    {
      onSuccess: () => {
        serviceHandlers.invalidateQueries(),
          successNotification({
            summary: t('strings.ATTACHED'),
            detail: t(`strings.CRUD_HELPER`, {
              attribute: t(`actions.DELETED`),
            }),
          })
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  return {
    loadRecords,
    addRecord,
    deleteRecords,
  }
}
